import { useState, useEffect, useCallback, useMemo } from "react";
import { supabaseAdmin as supabase } from "@/lib/supabaseAdmin";

interface Product {
  id: string;
  name: string;
  description?: string;
  price: number;
  selling_price: number;
  raised: number;
  target: number;
  stock_quantity: number;
  return_rate: number;
  status: string;
  tag?: string;
  category?: string;
  type: "trial" | "vip";
  image_url?: string;
}

interface AddProductForm {
  name: string;
  description: string;
  price: string;
  stock_quantity: string;
  selling_price: string;
  category: string;
  type: "trial" | "vip";
  image_url: string;
}

interface EditProductForm {
  id: string;
  name: string;
  description: string;
  price: string;
  stock_quantity: string;
  selling_price: string;
  category: string;
  type: "trial" | "vip";
  status: string;
  image_url: string;
}

export default function AdminSupply() {
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editProduct, setEditProduct] = useState<EditProductForm | null>(null);
  const [addForm, setAddForm] = useState<AddProductForm>({
    name: "",
    description: "",
    price: "",
    stock_quantity: "",
    selling_price: "",
    category: "服裝鞋帽",
    type: "trial",
    image_url: "",
  });
  const [addMsg, setAddMsg] = useState("");
  const [editMsg, setEditMsg] = useState("");
  const [addUploading, setAddUploading] = useState(false);
  const [editUploading, setEditUploading] = useState(false);
  const [activeZone, setActiveZone] = useState<"all" | "trial" | "vip">("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // 穩定 fetchProducts：useCallback + 空依賴，只執行一次
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError("");

    const { data, error: fetchError } = await supabase
      .from("products")
      .select("id, name, description, price, selling_price, raised, target, stock_quantity, return_rate, status, tag, category, type, image_url, created_at")
      .order("created_at", { ascending: false })
      .limit(10000);

    if (fetchError) {
      setError(`讀取貨源資料失敗：${fetchError.message}`);
    } else {
      setProducts(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const filtered = useMemo(() => {
    let result =
      activeZone === "all"
        ? products
        : products.filter((p) => p.type === activeZone);

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter((p) => {
        const fields = [
          p.id,
          p.name,
          p.description,
          p.category,
          p.status,
          String(p.price),
          String(p.selling_price),
        ];
        return fields.some((f) => (f || "").toLowerCase().includes(q));
      });
    }

    return result;
  }, [products, activeZone, searchQuery]);

  const handleAddImageUpload = async (file: File) => {
    setAddUploading(true);
    setAddMsg("");
    try {
      const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
      const allowed = ["jpg", "jpeg", "png", "webp"];
      if (!allowed.includes(ext)) {
        setAddMsg("僅支援 .jpg, .png, .webp 格式");
        setAddUploading(false);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setAddMsg("圖片大小超過 5MB 限制");
        setAddUploading(false);
        return;
      }
      const path = `products/${crypto.randomUUID()}.${ext}`;
      const { error: upError } = await supabase.storage
        .from("product-images")
        .upload(path, file, { contentType: file.type });
      if (upError) throw upError;
      const { data: urlData } = supabase.storage
        .from("product-images")
        .getPublicUrl(path);
      setAddForm((f) => ({ ...f, image_url: urlData.publicUrl }));
      setAddMsg("圖片上傳成功");
    } catch (err) {
      setAddMsg(`圖片上傳失敗：${err instanceof Error ? err.message : "未知錯誤"}`);
    } finally {
      setAddUploading(false);
    }
  };

  const handleEditImageUpload = async (file: File) => {
    setEditUploading(true);
    setEditMsg("");
    try {
      const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
      const allowed = ["jpg", "jpeg", "png", "webp"];
      if (!allowed.includes(ext)) {
        setEditMsg("僅支援 .jpg, .png, .webp 格式");
        setEditUploading(false);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setEditMsg("圖片大小超過 5MB 限制");
        setEditUploading(false);
        return;
      }
      const path = `products/${crypto.randomUUID()}.${ext}`;
      const { error: upError } = await supabase.storage
        .from("product-images")
        .upload(path, file, { contentType: file.type });
      if (upError) throw upError;
      const { data: urlData } = supabase.storage
        .from("product-images")
        .getPublicUrl(path);
      setEditProduct((p) => (p ? { ...p, image_url: urlData.publicUrl } : null));
      setEditMsg("圖片上傳成功");
    } catch (err) {
      setEditMsg(`圖片上傳失敗：${err instanceof Error ? err.message : "未知錯誤"}`);
    } finally {
      setEditUploading(false);
    }
  };

  const handleAdd = async () => {
    if (!addForm.name || !addForm.price || !addForm.stock_quantity) {
      setAddMsg("請填寫所有必填欄位");
      return;
    }

    const priceNum = Number(addForm.price);
    const sellingRaw = addForm.selling_price.trim();
    const spNum = sellingRaw !== "" ? Number(sellingRaw) : Math.round(priceNum * 1.25);

    const { data, error: insertError } = await supabase
      .from("products")
      .insert({
        name: addForm.name,
        description: addForm.description || null,
        price: priceNum,
        target: Number(addForm.stock_quantity) || 0,
        stock_quantity: Number(addForm.stock_quantity) || 0,
        return_rate: ((spNum - priceNum) / priceNum) * 100,
        selling_price: spNum,
      category: addForm.category,
        type: addForm.type,
        status: "active",
        tag: addForm.type === "trial" ? "新手專屬體驗" : "VIP 限定貨源",
        raised: 0,
        image_url: addForm.image_url || null,
      })
      .select()
      .single();

    if (insertError) {
      setAddMsg(`新增失敗：${insertError.message}`);
      return;
    }

    if (data) {
      setProducts((prev) => [data as Product, ...prev]);
      setAddMsg("新增成功！列表已自動更新");
      setAddForm({
        name: "",
        description: "",
        price: "",
        stock_quantity: "",
        selling_price: "",
        category: "服裝鞋帽",
        type: "trial",
        image_url: "",
      });
      setTimeout(() => {
        setShowAdd(false);
        setAddMsg("");
      }, 1200);
    }
  };

  const openEdit = (product: Product) => {
    setEditProduct({
      id: product.id,
      name: product.name,
      description: product.description || "",
      price: String(product.price),
      stock_quantity: String(product.stock_quantity || 0),
      selling_price: product.selling_price != null ? String(product.selling_price) : String(Math.round(product.price * 1.25)),
      category: product.category || "服裝鞋帽",
      type: product.type,
      status: product.status,
      image_url: product.image_url || "",
    });
    setEditMsg("");
    setEditUploading(false);
    setShowEdit(true);
  };

  const handleEdit = async () => {
    if (!editProduct) return;
    if (!editProduct.name || !editProduct.price || !editProduct.stock_quantity) {
      setEditMsg("請填寫所有必填欄位");
      return;
    }

    const priceNum = Number(editProduct.price);
    const sellingRaw = editProduct.selling_price.trim();
    const spNum = sellingRaw !== "" ? Number(sellingRaw) : Math.round(priceNum * 1.25);

    const { data, error: updateError } = await supabase
      .from("products")
      .update({
        name: editProduct.name,
        description: editProduct.description || null,
        price: priceNum,
        target: Number(editProduct.stock_quantity) || 0,
        stock_quantity: Number(editProduct.stock_quantity) || 0,
        return_rate: ((spNum - priceNum) / priceNum) * 100,
        selling_price: spNum,
        category: editProduct.category,
        type: editProduct.type,
        status: editProduct.status,
        image_url: editProduct.image_url || null,
      })
      .eq("id", editProduct.id)
      .select()
      .single();

    if (updateError) {
      setEditMsg(`更新失敗：${updateError.message}`);
      return;
    }

    if (data) {
      setProducts((prev) =>
        prev.map((p) => (p.id === editProduct.id ? (data as Product) : p))
      );
      setEditMsg("更新成功！");
      setTimeout(() => {
        setShowEdit(false);
        setEditProduct(null);
        setEditMsg("");
      }, 1200);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm("確定要刪除此貨源嗎？")) {
      const { error: deleteError } = await supabase
        .from("products")
        .delete()
        .eq("id", id);

      if (deleteError) {
        setError(`刪除失敗：${deleteError.message}`);
      } else {
        setProducts((prev) => prev.filter((p) => p.id !== id));
      }
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Header bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2 bg-white rounded-lg border border-navy-100 p-1">
          {[
            { key: "all", label: "全部" },
            { key: "trial", label: "體驗區" },
            { key: "vip", label: "VIP 區" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveZone(tab.key as typeof activeZone)}
              className={`font-body text-xs font-medium px-3 py-1.5 rounded-md whitespace-nowrap cursor-pointer ${
                activeZone === tab.key
                  ? "bg-navy-700 text-white"
                  : "text-navy-500 hover:text-navy-700 hover:bg-navy-50"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-initial">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-navy-400 text-sm" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="搜尋商品名稱、分類、價格..."
              className="w-full sm:w-64 font-body text-sm pl-9 pr-9 py-2 rounded-lg border border-navy-200 focus:border-navy-500 outline-none bg-white"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full hover:bg-navy-100 text-navy-400 hover:text-navy-700 cursor-pointer"
              >
                <i className="ri-close-line text-sm" />
              </button>
            )}
          </div>
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 font-body text-sm font-semibold px-4 py-2.5 rounded-lg bg-navy-700 text-white hover:bg-navy-800 whitespace-nowrap cursor-pointer shrink-0"
          >
            <i className="ri-add-line" />
            新增貨源
          </button>
        </div>
      </div>

      {!loading && searchQuery.trim() && (
        <div className="flex items-center gap-2">
          <span className="font-body text-xs text-navy-500">
            找到 {filtered.length} 筆商品
          </span>
          {filtered.length === 0 && (
            <span className="font-body text-xs text-navy-400">
              沒有符合「{searchQuery.trim()}」的搜尋結果
            </span>
          )}
        </div>
      )}

      {error && (
        <div className="font-body text-sm px-4 py-2.5 rounded-lg bg-red-50 text-red-600 border border-red-200">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-navy-100 bg-navy-50/50">
                {["貨源名稱", "分類", "認購價", "銷售價", "利潤", "庫存數量", "庫存", "狀態", "操作"].map((h) => (
                  <th key={h} className="font-body text-[11px] font-semibold text-navy-600 text-left px-4 py-3 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-navy-100">
              {loading && (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center">
                    <p className="font-body text-sm text-navy-400">載入中...</p>
                  </td>
                </tr>
              )}

              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center">
                    <p className="font-body text-sm text-navy-400">
                      {searchQuery.trim() ? "沒有符合搜尋結果的商品" : "暫無貨源資料"}
                    </p>
                    {searchQuery.trim() && (
                      <button
                        onClick={() => setSearchQuery("")}
                        className="mt-2 font-body text-xs text-navy-500 hover:text-navy-700 underline cursor-pointer"
                      >
                        清除搜尋
                      </button>
                    )}
                  </td>
                </tr>
              )}

              {!loading &&
                filtered.map((p) => {
                  const remaining = p.stock_quantity - p.raised;
                  const profit = p.selling_price - p.price;
                  const isVip = p.type === "vip";
                  return (
                    <tr key={p.id}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-navy-100 flex items-center justify-center shrink-0 overflow-hidden">
                            {p.image_url ? (
                              <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                            ) : (
                              <i className="ri-box-3-line text-navy-500 text-sm" />
                            )}
                          </div>
                          <div>
                            <p className="font-body text-sm font-medium text-navy-900">{p.name}</p>
                            <p className="font-body text-[10px] text-navy-400 truncate max-w-[180px]">
                              {p.description?.slice(0, 30) || "—"}...
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-body text-[10px] font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                          {p.category || "未分類"}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-body text-sm text-navy-900">NT$ {p.price.toLocaleString()}</td>
                      <td className="px-4 py-3 font-body text-sm text-navy-900">NT$ {(p.selling_price).toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <span className="font-body text-xs font-semibold text-emerald-600">NT$ {profit.toLocaleString()}</span>
                      </td>
                      <td className="px-4 py-3 font-body text-sm text-navy-700">{p.stock_quantity || 0} 件</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-body text-[10px] text-navy-400 w-20">{p.stock_quantity || 0} 件</span>
                          <div className="w-20 h-1.5 rounded-full bg-navy-100 overflow-hidden">
                            <div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.min(Math.round((p.raised / p.target) * 100), 100)}%` }} />
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`font-body text-[10px] font-semibold px-2 py-1 rounded-full border ${
                          p.status === "active"
                            ? "bg-emerald-50 text-emerald-600 border-emerald-200"
                            : "bg-navy-50 text-navy-500 border-navy-200"
                        }`}>
                          {p.status === "active" ? "供應中" : "已結束"}
                        </span>
                        {isVip && (
                          <span className="font-body text-[10px] font-semibold px-2 py-1 rounded-full bg-amber-50 text-amber-600 border border-amber-200 ml-1.5">
                            VIP
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => openEdit(p)}
                            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-navy-100 text-navy-400 hover:text-navy-700 cursor-pointer"
                          >
                            <i className="ri-pencil-line text-sm" />
                          </button>
                          <button
                            onClick={() => handleDelete(p.id)}
                            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-red-50 text-navy-400 hover:text-red-500 cursor-pointer"
                          >
                            <i className="ri-delete-bin-line text-sm" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Product Modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-2xl border border-navy-100 shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-5 border-b border-navy-100 flex items-center justify-between">
              <h3 className="font-display text-base font-bold text-navy-900">新增貨源</h3>
              <button
                onClick={() => { setShowAdd(false); setAddMsg(""); }}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-navy-50 text-navy-400 hover:text-navy-700 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="p-6 flex flex-col gap-4">
              {addMsg && (
                <p className={`font-body text-sm ${addMsg.includes("成功") ? "text-emerald-600" : "text-red-500"}`}>
                  {addMsg}
                </p>
              )}
              <div>
                <label className="font-body text-xs font-medium text-navy-700 mb-1 block">所屬區域</label>
                <div className="flex gap-2">
                  {(["trial", "vip"] as const).map((z) => (
                    <button
                      key={z}
                      onClick={() => setAddForm((f) => ({ ...f, type: z }))}
                      className={`flex-1 py-2 rounded-lg font-body text-sm cursor-pointer ${
                        addForm.type === z
                          ? "bg-navy-700 text-white"
                          : "border border-navy-200 text-navy-600 hover:border-navy-400"
                      }`}
                    >
                      {z === "trial" ? "體驗區" : "VIP 區"}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="font-body text-xs font-medium text-navy-700 mb-1 block">
                  貨源名稱 <span className="text-red-400">*</span>
                </label>
                <input
                  value={addForm.name}
                  onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="例如：限量潮牌球鞋"
                  className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 focus:border-navy-500 outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-body text-xs font-medium text-navy-700 mb-1 block">商品分類</label>
                  <select
                    value={addForm.category}
                    onChange={(e) => setAddForm((f) => ({ ...f, category: e.target.value }))}
                    className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 focus:border-navy-500 outline-none bg-white"
                  >
                    <option value="服裝鞋帽">服裝鞋帽</option>
                    <option value="生活用品">生活用品</option>
                    <option value="寵物用品">寵物用品</option>
                    <option value="母嬰用品">母嬰用品</option>
                    <option value="家電用品">家電用品</option>
                    <option value="食品">食品</option>
                    <option value="手機電腦週邊">手機電腦週邊</option>
                    <option value="保健醫療">保健醫療</option>
                    <option value="美妝保養">美妝保養</option>
                    <option value="傢俱寢具">傢俱寢具</option>
                  </select>
                </div>
                <div>
                  <label className="font-body text-xs font-medium text-navy-700 mb-1 block">銷售價</label>
                  <input
                    type="number"
                    value={addForm.selling_price}
                    onChange={(e) => setAddForm((f) => ({ ...f, selling_price: e.target.value }))}
                    placeholder="12800"
                    className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 focus:border-navy-500 outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="font-body text-xs font-medium text-navy-700 mb-1 block">描述</label>
                <textarea
                  value={addForm.description}
                  onChange={(e) => setAddForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="貨源詳細描述..."
                  maxLength={200}
                  rows={2}
                  className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 focus:border-navy-500 outline-none resize-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-body text-xs font-medium text-navy-700 mb-1 block">
                    認購價 <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="number"
                    value={addForm.price}
                    onChange={(e) => setAddForm((f) => ({ ...f, price: e.target.value }))}
                    placeholder="12800"
                    className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 focus:border-navy-500 outline-none"
                  />
                </div>
                <div>
                  <label className="font-body text-xs font-medium text-navy-700 mb-1 block">
                    必購組數 <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="number"
                    value={addForm.stock_quantity}
                    onChange={(e) => setAddForm((f) => ({ ...f, stock_quantity: e.target.value }))}
                    placeholder="100"
                    className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 focus:border-navy-500 outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="font-body text-xs font-medium text-navy-700 mb-1 block">商品圖片上傳</label>
                <div className="flex items-center gap-3">
                  <label className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed transition-colors ${
                    addUploading
                      ? "border-navy-200 bg-navy-50 cursor-not-allowed"
                      : "border-navy-200 hover:border-navy-400 hover:bg-navy-50 cursor-pointer"
                  }`}>
                    <i className={`ri-upload-cloud-2-line ${addUploading ? "text-navy-300" : "text-navy-400"}`} />
                    <span className={`font-body text-sm ${addUploading ? "text-navy-300" : "text-navy-500"}`}>
                      {addUploading ? "上傳中..." : "點擊或拖曳上傳 .jpg / .png / .webp"}
                    </span>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleAddImageUpload(file);
                        e.target.value = "";
                      }}
                      disabled={addUploading}
                    />
                  </label>
                </div>
                {addForm.image_url && (
                  <div className="mt-3 w-full h-32 rounded-lg border border-navy-200 overflow-hidden bg-navy-50">
                    <img src={addForm.image_url} alt="預覽" className="w-full h-full object-contain" loading="lazy" decoding="async" />
                  </div>
                )}
              </div>
              <div>
                <label className="font-body text-xs font-medium text-navy-700 mb-1 block">商品圖片網址</label>
                <input
                  value={addForm.image_url}
                  onChange={(e) => setAddForm((f) => ({ ...f, image_url: e.target.value }))}
                  placeholder="https://example.com/image.jpg"
                  className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 focus:border-navy-500 outline-none"
                />
                <p className="font-body text-[10px] text-navy-400 mt-1">貼上圖片 URL 或直接上傳檔案，前台會顯示此圖片</p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-navy-100 flex justify-end gap-2">
              <button
                onClick={() => { setShowAdd(false); setAddMsg(""); }}
                className="font-body text-sm font-medium px-4 py-2.5 rounded-lg border border-navy-200 text-navy-600 hover:bg-navy-50 cursor-pointer"
              >
                取消
              </button>
              <button
                onClick={handleAdd}
                disabled={addUploading}
                className="font-body text-sm font-semibold px-5 py-2.5 rounded-lg bg-navy-700 text-white hover:bg-navy-800 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {addUploading ? "上傳中..." : "確認新增"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Product Modal */}
      {showEdit && editProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-2xl border border-navy-100 shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-5 border-b border-navy-100 flex items-center justify-between">
              <h3 className="font-display text-base font-bold text-navy-900">編輯貨源</h3>
              <button
                onClick={() => { setShowEdit(false); setEditProduct(null); setEditMsg(""); }}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-navy-50 text-navy-400 hover:text-navy-700 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="p-6 flex flex-col gap-4">
              {editMsg && (
                <p className={`font-body text-sm ${editMsg.includes("成功") ? "text-emerald-600" : "text-red-500"}`}>
                  {editMsg}
                </p>
              )}
              <div>
                <label className="font-body text-xs font-medium text-navy-700 mb-1 block">所屬區域</label>
                <div className="flex gap-2">
                  {(["trial", "vip"] as const).map((z) => (
                    <button
                      key={z}
                      onClick={() => setEditProduct((p) => p ? { ...p, type: z } : null)}
                      className={`flex-1 py-2 rounded-lg font-body text-sm cursor-pointer ${
                        editProduct.type === z
                          ? "bg-navy-700 text-white"
                          : "border border-navy-200 text-navy-600 hover:border-navy-400"
                      }`}
                    >
                      {z === "trial" ? "體驗區" : "VIP 區"}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="font-body text-xs font-medium text-navy-700 mb-1 block">
                  貨源名稱 <span className="text-red-400">*</span>
                </label>
                <input
                  value={editProduct.name}
                  onChange={(e) => setEditProduct((p) => p ? { ...p, name: e.target.value } : null)}
                  placeholder="例如：限量潮牌球鞋"
                  className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 focus:border-navy-500 outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-body text-xs font-medium text-navy-700 mb-1 block">商品分類</label>
                  <select
                    value={editProduct.category}
                    onChange={(e) => setEditProduct((p) => p ? { ...p, category: e.target.value } : null)}
                    className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 focus:border-navy-500 outline-none bg-white"
                  >
                    <option value="服裝鞋帽">服裝鞋帽</option>
                    <option value="生活用品">生活用品</option>
                    <option value="寵物用品">寵物用品</option>
                    <option value="母嬰用品">母嬰用品</option>
                    <option value="家電用品">家電用品</option>
                    <option value="食品">食品</option>
                    <option value="手機電腦週邊">手機電腦週邊</option>
                    <option value="保健醫療">保健醫療</option>
                    <option value="美妝保養">美妝保養</option>
                    <option value="傢俱寢具">傢俱寢具</option>
                  </select>
                </div>
                <div>
                  <label className="font-body text-xs font-medium text-navy-700 mb-1 block">狀態</label>
                  <select
                    value={editProduct.status}
                    onChange={(e) => setEditProduct((p) => p ? { ...p, status: e.target.value } : null)}
                    className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 focus:border-navy-500 outline-none bg-white"
                  >
                    <option value="active">供應中</option>
                    <option value="inactive">已結束</option>
                  </select>
                </div>
              </div>
                <div>
                  <label className="font-body text-xs font-medium text-navy-700 mb-1 block">商品圖片上傳</label>
                  <div className="flex items-center gap-3">
                    <label className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed transition-colors ${
                      editUploading
                        ? "border-navy-200 bg-navy-50 cursor-not-allowed"
                        : "border-navy-200 hover:border-navy-400 hover:bg-navy-50 cursor-pointer"
                    }`}>
                      <i className={`ri-upload-cloud-2-line ${editUploading ? "text-navy-300" : "text-navy-400"}`} />
                      <span className={`font-body text-sm ${editUploading ? "text-navy-300" : "text-navy-500"}`}>
                        {editUploading ? "上傳中..." : "點擊或拖曳上傳 .jpg / .png / .webp"}
                      </span>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleEditImageUpload(file);
                          e.target.value = "";
                        }}
                        disabled={editUploading}
                      />
                    </label>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-body text-xs font-medium text-navy-700 mb-1 block">銷售價</label>
                  <input
                    type="number"
                    value={editProduct.selling_price}
                    onChange={(e) => setEditProduct((p) => p ? { ...p, selling_price: e.target.value } : null)}
                    placeholder="12800"
                    className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 focus:border-navy-500 outline-none"
                  />
                </div>
                <div>
                  <label className="font-body text-xs font-medium text-navy-700 mb-1 block">商品圖片網址</label>
                  <input
                    value={editProduct.image_url}
                    onChange={(e) => setEditProduct((p) => p ? { ...p, image_url: e.target.value } : null)}
                    placeholder="https://example.com/image.jpg"
                    className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 focus:border-navy-500 outline-none"
                  />
                </div>
              </div>
              {editProduct.image_url && (
                <div className="w-full h-32 rounded-lg border border-navy-200 overflow-hidden bg-navy-50">
                  <img src={editProduct.image_url} alt="預覽" className="w-full h-full object-contain" loading="lazy" decoding="async" />
                </div>
              )}
              <div>
                <label className="font-body text-xs font-medium text-navy-700 mb-1 block">描述</label>
                <textarea
                  value={editProduct.description}
                  onChange={(e) => setEditProduct((p) => p ? { ...p, description: e.target.value } : null)}
                  placeholder="貨源詳細描述..."
                  maxLength={200}
                  rows={2}
                  className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 focus:border-navy-500 outline-none resize-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-body text-xs font-medium text-navy-700 mb-1 block">
                    認購價 <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="number"
                    value={editProduct.price}
                    onChange={(e) => setEditProduct((p) => p ? { ...p, price: e.target.value } : null)}
                    placeholder="12800"
                    className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 focus:border-navy-500 outline-none"
                  />
                </div>
                <div>
                  <label className="font-body text-xs font-medium text-navy-700 mb-1 block">
                    必購組數 <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="number"
                    value={editProduct.stock_quantity}
                    onChange={(e) => setEditProduct((p) => p ? { ...p, stock_quantity: e.target.value } : null)}
                    placeholder="100"
                    className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 focus:border-navy-500 outline-none"
                  />
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-navy-100 flex justify-end gap-2">
              <button
                onClick={() => { setShowEdit(false); setEditProduct(null); setEditMsg(""); }}
                className="font-body text-sm font-medium px-4 py-2.5 rounded-lg border border-navy-200 text-navy-600 hover:bg-navy-50 cursor-pointer"
              >
                取消
              </button>
              <button
                onClick={handleEdit}
                disabled={editUploading}
                className="font-body text-sm font-semibold px-5 py-2.5 rounded-lg bg-navy-700 text-white hover:bg-navy-800 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {editUploading ? "上傳中..." : "確認更新"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}